/**
 * browser 上の portfolio DB 接続と transaction 実行を管理する。
 */
import {
  getPortfolioFromLocalStorage,
  getPortfolioUpdatedAtFromLocalStorage,
  markPortfolioDbDirtyInLocalStorage,
} from '@/localStorage'
import type { RunToDatabase } from '@/types'
import {
  CorruptedDatabaseFileException,
  PortfolioChangedException,
} from '@pkg/basic'
import { type DatabaseWithTransaction, schema } from '@pkg/database-portfolio'
import { sql } from 'drizzle-orm'
import { drizzle } from 'drizzle-orm/sqlite-proxy'
import { SQLocal } from 'sqlocal'
import { SQLocalDrizzle } from 'sqlocal/drizzle'
import { handleCorruptedPortfolioDatabase } from './corruptedDatabase'
import { executeTransactionQuerySteps } from './executeTransactionQuerySteps'
import { Semaphore } from './semaphore'

let alreadyAlerted = false
let isForbiddenUsingDatabase = false

let cache:
  | {
      id: string
      database: DatabaseWithTransaction
      destroy: () => Promise<void>
    }
  | undefined
const semaphore = new Semaphore(1)

export const forbidUsingDatabase = () => {
  isForbiddenUsingDatabase = true
}

export const getIsForbiddenUsingDatabase = () => isForbiddenUsingDatabase

const createPortfolioDbFileName = (id: string): string => `${id}.sqlite`

type SQLocalQueryMethod = 'run' | 'all' | 'values' | 'get'

type SQLocalQueryStatement = {
  readonly sql: string
  readonly params: readonly unknown[]
  readonly method: SQLocalQueryMethod
}

type SQLocalQueryRow = readonly unknown[] | Record<string, unknown>

type SQLocalTransactionContext = {
  readonly query: (
    statement: SQLocalQueryStatement,
  ) => Promise<readonly SQLocalQueryRow[]>
}

/**
 * Drizzle sqlite-proxy の get no-row runtime contract に合わせる。
 * @returns Drizzle が no row と解釈する falsy value を返す。
 */
const createMissingGetRowForDrizzleProxy = (): unknown[] => JSON.parse('null')

/**
 * SQLocal の row を drizzle sqlite-proxy が期待する array row へ変換する。
 * @param row SQLocal transaction query が返した 1 row を受け取る。
 * @returns column order の numeric index と column 名 property を持つ配列を返す。
 */
const normalizeSqlocalQueryRowForDrizzleProxy = (
  row: SQLocalQueryRow,
): unknown[] => {
  if (Array.isArray(row)) {
    return [...row]
  }

  const normalizedRow = Object.values(row)
  for (const [columnName, value] of Object.entries(row)) {
    Object.defineProperty(normalizedRow, columnName, {
      configurable: true,
      enumerable: false,
      value,
    })
  }

  return normalizedRow
}

/**
 * SQLocal の row 一覧を drizzle sqlite-proxy の method 別 contract に合わせる。
 * @param params SQLocal rows と query method を受け取る。
 * @returns get では 1 row array、それ以外では array row の配列を返す。
 */
const normalizeSqlocalQueryRowsForDrizzleProxy = ({
  rows,
  method,
}: {
  readonly rows: readonly SQLocalQueryRow[]
  readonly method: SQLocalQueryMethod
}): unknown[] | unknown[][] => {
  const normalizedRows = rows.map(normalizeSqlocalQueryRowForDrizzleProxy)

  if (method === 'get') {
    return normalizedRows[0] ?? createMissingGetRowForDrizzleProxy()
  }

  return normalizedRows
}

/**
 * SQLocal transaction を drizzle transaction database へ変換する。
 * @param params SQLocal transaction context を受け取る。
 * @returns browser query step が利用する transaction database を返す。
 */
export const createTransactionDatabaseFromSqlocalTx = (params: {
  readonly tx: SQLocalTransactionContext
}): DatabaseWithTransaction['database'] => {
  const { tx } = params

  return drizzle(
    async (sqlText, params, method) => {
      const rows = await tx.query({
        sql: sqlText,
        params,
        method,
      })
      return {
        rows: normalizeSqlocalQueryRowsForDrizzleProxy({
          rows,
          method,
        }),
      }
    },
    async (queries) =>
      await Promise.all(
        queries.map(async (query) => {
          const rows = await tx.query(query)
          return {
            rows: normalizeSqlocalQueryRowsForDrizzleProxy({
              rows,
              method: query.method,
            }),
          }
        }),
      ),
    { schema },
  )
}

/**
 * SQLocalDrizzle を使った browser 用 database connection を作成する。
 * @param params 接続先 file 名を受け取る。
 * @returns database と destroy 関数を返す。
 */
export const createSqlocalDatabaseWithTransaction = async (params: {
  readonly fileName: string
}): Promise<{
  readonly database: DatabaseWithTransaction
  readonly destroy: () => Promise<void>
}> => {
  const { fileName } = params
  const { driver, batchDriver, transaction, destroy } = new SQLocalDrizzle(
    fileName,
  )
  const database = drizzle(driver, batchDriver, { schema })
  await database.run(sql`PRAGMA foreign_keys=ON;`)

  return {
    database: {
      database,
      transaction: async (querySteps) =>
        await transaction(async (tx) => {
          const transactionDatabase = createTransactionDatabaseFromSqlocalTx({
            tx,
          })

          await executeTransactionQuerySteps({
            querySteps,
            transactionDatabase,
          })
        }),
    },
    destroy,
  }
}

const getDatabase = async (
  id: string,
): Promise<{
  database: DatabaseWithTransaction
  destroy: () => Promise<void>
}> =>
  await createSqlocalDatabaseWithTransaction({
    fileName: createPortfolioDbFileName(id),
  })

/**
 * 開いている portfolio が操作対象と一致することを検証する。
 * 不一致なら navigation を予約した上で例外を投げ、古い portfolio への操作継続を防ぐ。
 * window.location.replace は navigation を予約するだけで同期的に script を止めないため、
 * 呼び出し元の処理を確実に中断するには例外が必要になる。
 * @param params 操作対象 portfolio ID を受け取る。
 */
const assertPortfolioIdentity = ({ id }: { readonly id: string }): void => {
  const portfolio = getPortfolioFromLocalStorage()
  if (portfolio !== undefined && portfolio.uuid === id) {
    return
  }
  if (!alreadyAlerted) {
    alreadyAlerted = true
    alert('The opened portfolio has been changed!')
  }
  window.location.replace(window.location.pathname)
  throw new PortfolioChangedException({
    expectedPortfolioId: id,
    currentPortfolioId: portfolio?.uuid,
  })
}

/**
 * 破損調査用に OPFS 上の DB file size を取得する。
 * 取得できない場合も破損報告を妨げない。
 * @param params 対象 portfolio ID を受け取る。
 * @returns file size (byte)。取得できなければ undefined を返す。
 */
const getDatabaseFileSizeForReport = async ({
  id,
}: {
  readonly id: string
}): Promise<number | undefined> => {
  try {
    const root = await navigator.storage.getDirectory()
    const fileHandle = await root.getFileHandle(createPortfolioDbFileName(id))
    const file = await fileHandle.getFile()
    return file.size
  } catch {
    return undefined
  }
}

/**
 * file snapshot 取得前に portfolio identity、接続、破損状態を検証する。
 * database semaphore を取得済みの呼び出し元からだけ利用する。
 * @param params 対象 portfolio ID を受け取る。
 */
const prepareDatabaseFileAccess = async ({
  id,
}: {
  readonly id: string
}): Promise<void> => {
  if (isForbiddenUsingDatabase) {
    throw new Error('Connection to the database is forbidden')
  }
  assertPortfolioIdentity({ id })
  if (cache !== undefined && cache.id !== id) {
    await cache.destroy()
    cache = undefined
  }
  if (cache === undefined) {
    const database = await getDatabase(id)
    cache = {
      ...database,
      id,
    }
  }
  let tableNames: readonly string[] | undefined
  try {
    const tableRows = await cache.database.database.values(
      sql`SELECT \`name\` FROM \`sqlite_master\` WHERE \`type\` = 'table';`,
    )
    tableNames = tableRows
      .flat()
      .filter((name): name is string => typeof name === 'string')
    if (!tableNames.includes('portfolio')) {
      throw new CorruptedDatabaseFileException()
    }
  } catch (error) {
    if (error instanceof CorruptedDatabaseFileException) {
      await handleCorruptedPortfolioDatabase({
        error,
        site: 'database-file-access',
        context: {
          portfolioId: id,
          tableNames,
          fileSizeBytes: await getDatabaseFileSizeForReport({ id }),
        },
      })
    }
    throw error
  }
}

/** database semaphore 内で取得した SQLite file と対応 timestamp。 */
export type DatabaseSnapshot = {
  readonly file: File
  readonly updatedAt: number
}

/**
 * SQLite file と対応する local update timestamp を同じ排他区間で取得する。
 * @param params 対象 portfolio ID を受け取る。
 * @returns database file snapshot とその updatedAt を返す。
 */
export const getDatabaseSnapshot = async ({
  id,
}: {
  readonly id: string
}): Promise<DatabaseSnapshot> => {
  await semaphore.acquire()

  try {
    await prepareDatabaseFileAccess({ id })
    const updatedAt = getPortfolioUpdatedAtFromLocalStorage() ?? 0
    const root = await navigator.storage.getDirectory()
    const fileHandle = await root.getFileHandle(createPortfolioDbFileName(id))
    const file = await fileHandle.getFile()
    return { file, updatedAt }
  } finally {
    semaphore.release()
  }
}

/**
 * 既存 caller 向けに SQLite file snapshot だけを取得する。
 * @param id 対象 portfolio ID を受け取る。
 * @returns database file snapshot を返す。
 */
export const getDatabaseFile = async (id: string): Promise<File> => {
  const snapshot = await getDatabaseSnapshot({ id })
  return snapshot.file
}

export const deleteDatabase = async (id: string) => {
  await semaphore.acquire()

  try {
    if (isForbiddenUsingDatabase) {
      throw new Error('Connection to the database is forbidden')
    }
    if (cache !== undefined) {
      await cache.destroy()
      cache = undefined
    }
    const { deleteDatabaseFile, destroy } = new SQLocal(
      createPortfolioDbFileName(id),
    )
    try {
      await deleteDatabaseFile()
    } finally {
      await destroy()
    }
  } finally {
    semaphore.release()
  }
}

export const overwriteDatabase = async (
  id: string,
  fileStream: ReadableStream,
) => {
  await semaphore.acquire()

  try {
    if (isForbiddenUsingDatabase) {
      throw new Error('Connection to the database is forbidden')
    }
    if (cache !== undefined) {
      await cache.destroy()
      cache = undefined
    }
    const decompressed = fileStream.pipeThrough(new DecompressionStream('gzip'))
    const root = await navigator.storage.getDirectory()
    const fileHandle = await root.getFileHandle(createPortfolioDbFileName(id), {
      create: true,
    })
    const writable = await fileHandle.createWritable()
    const logIntervalMs = 1000
    let processed = 0
    let lastLog = Date.now()
    const countingWritable = new TransformStream<Uint8Array, Uint8Array>({
      transform(chunk: Uint8Array, controller) {
        const len = chunk?.byteLength ?? 0
        processed += len
        if (Date.now() - lastLog >= logIntervalMs) {
          console.warn(`processed ${processed} bytes`)
          lastLog = Date.now()
        }
        controller.enqueue(chunk)
      },
    })
    const started = Date.now()
    await decompressed.pipeThrough(countingWritable).pipeTo(writable)
    console.warn(`${Date.now() - started}ms: OPFS writable stream finished`)
  } finally {
    semaphore.release()
  }
}

export const createReadFromDatabase =
  (id: string): RunToDatabase =>
  async (run) => {
    await semaphore.acquire()

    try {
      if (isForbiddenUsingDatabase) {
        throw new Error('Connection to the database is forbidden')
      }
      assertPortfolioIdentity({ id })
      if (cache !== undefined && cache.id !== id) {
        await cache.destroy()
        cache = undefined
      }
      if (cache === undefined) {
        const database = await getDatabase(id)
        cache = {
          ...database,
          id,
        }
      }
      const result = await run(cache.database)
      return result
    } finally {
      semaphore.release()
    }
  }

/**
 * database write を排他的に実行し、成功した場合だけ dirty 状態を記録する。
 * @param id write 対象の portfolio ID を受け取る。
 * @returns database write runner を返す。
 */
export const createWriteToDatabase =
  (id: string): RunToDatabase =>
  async (run) => {
    await semaphore.acquire()

    try {
      if (isForbiddenUsingDatabase) {
        throw new Error('Connection to the database is forbidden')
      }
      assertPortfolioIdentity({ id })
      if (cache !== undefined && cache.id !== id) {
        await cache.destroy()
        cache = undefined
      }
      if (cache === undefined) {
        const database = await getDatabase(id)
        cache = {
          ...database,
          id,
        }
      }
      const result = await run(cache.database)
      const updatedAt = Date.now()
      markPortfolioDbDirtyInLocalStorage({ portfolioId: id, updatedAt })
      return result
    } finally {
      semaphore.release()
    }
  }
